import { Checkbox, FormControlLabel, Grid, ListSubheader, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import React, { useContext } from 'react'
import StatesAndProvinces from '../../shared/classes/StatesAndProvinces'
import { AppContext } from '../context/AppContextProvider'

const GuestForm = React.forwardRef(function _GuestForm(props, ref) {
    const { iframeFixes } = useContext(AppContext)
    const { defaults = {}, optionals = [], onInput, onSubmit, disabled, children, ...otherProps } = props

    return (
        <form {...otherProps} ref={ref} onSubmit={onSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        id="firstName"
                        name="firstName"
                        defaultValue={defaults?.firstName || ''}
                        label="First Name"
                        variant="outlined"
                        autoComplete="fname"
                        required={optionals.includes('firstName') == false}
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        id="lastName"
                        name="lastName"
                        defaultValue={defaults?.lastName || ''}
                        label="Last Name"
                        variant="outlined"
                        autoComplete="lname"
                        required={optionals.includes('lastName') == false}
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="email"
                        name="email"
                        defaultValue={defaults?.email || ''}
                        label="Email Address"
                        variant="outlined"
                        type="email"
                        inputProps={{
                            // default pattern doesn't require .tld
                            pattern: "[a-z0-9._%+\\-]+@[a-z0-9.\\-]+\\.[a-z]{2,4}$"
                        }}
                        autoComplete="email"
                        required={optionals.includes('email') == false}
                        fullWidth
                        onInput={event => {
                            event.target.setCustomValidity('')

                            // set custom validity message
                            if (!event.target.validity.valid && !event.target.validity.valueMissing) {
                                event.target.setCustomValidity('Invalid email address')
                            }

                            onInput && onInput(event)
                        }}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="phone"
                        name="phone"
                        defaultValue={defaults?.phone || ''}
                        label="Phone/Mobile Number"
                        variant="outlined"
                        type="tel"
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "\\d{10}",
                            minLength: 10,
                            maxLength: 10,
                        }}
                        autoComplete="mobile"
                        required={optionals.includes('phone') == false}
                        fullWidth
                        onInput={event => {
                            event.target.setCustomValidity('')

                            // set custom validity message
                            if (!event.target.validity.valid && !event.target.validity.valueMissing) {
                                event.target.setCustomValidity('Phone numbers must be in the format 1231231234')
                            }

                            onInput && onInput(event)
                        }}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Tooltip disableInteractive arrow title="SMS messages are primarily used for appointment reminders and communication with staff members.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="allow_sms"
                                    name="allow_sms"
                                    color="primary"
                                    defaultChecked
                                />
                            }
                            label={<>
                                <Typography display="block">I give consent to receive SMS messages.</Typography>
                                <Typography display="block" variant="caption">Message and data rates may apply. Reply STOP to opt out.</Typography>
                            </>}
                        />
                    </Tooltip>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="street1"
                        name="street1"
                        defaultValue={defaults?.street1 || ''}
                        label="Address Line 1"
                        variant="outlined"
                        type="text"
                        autoComplete="street1"
                        required={optionals.includes('street1') == false}
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="street2"
                        name="street2"
                        defaultValue={defaults?.street2 || ''}
                        label="Address Line 2"
                        variant="outlined"
                        type="text"
                        autoComplete="street2"
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="city"
                        name="city"
                        defaultValue={defaults?.city || ''}
                        label="City"
                        variant="outlined"
                        type="text"
                        autoComplete="city"
                        required={optionals.includes('city') == false}
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        SelectProps={iframeFixes.Select}
                        select={true}
                        id="state"
                        name="state"
                        defaultValue={defaults?.state || ''}
                        label="State / Province"
                        variant="outlined"
                        autoComplete="state"
                        required={optionals.includes('state') == false}
                        fullWidth
                        onInput={onInput}
                        disabled={disabled}
                    >
                        <ListSubheader>USA</ListSubheader>
                        {Object.keys(StatesAndProvinces.usNames).map(abbrev => (
                            <MenuItem key={abbrev} value={abbrev}>{StatesAndProvinces.usNames[abbrev]}</MenuItem>
                        ))}
                        <ListSubheader>Canada</ListSubheader>
                        {Object.keys(StatesAndProvinces.caNames).map(abbrev => (
                            <MenuItem key={abbrev} value={abbrev}>{StatesAndProvinces.caNames[abbrev]}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="zip"
                        name="zip"
                        defaultValue={defaults?.zip || ''}
                        label="Zip / Postal Code"
                        variant="outlined"
                        type="text"
                        autoComplete="zip"
                        required={optionals.includes('zip') == false}
                        fullWidth
                        inputProps={{
                            pattern: "(^\\d{5}(-\\d{4})?$)|(^[A-Za-z]\\d[A-Za-z] \\d[A-Za-z]\\d$)",
                        }}
                        onInput={event => {
                            event.target.setCustomValidity('')

                            // set custom validity message
                            if (!event.target.validity.valid && !event.target.validity.valueMissing) {
                                event.target.setCustomValidity('Zip / Postal Codes must be in the format 12345, 12345-6789, or A1B 2C3.')
                            }

                            onInput && onInput(event)
                        }}
                        disabled={disabled}
                    />
                </Grid>
                {children}
            </Grid>
        </form>
    )
})

export default GuestForm
